import { useEffect, useState, useRef } from "react";
import { Search, Flame, Star, X, Loader2, Copy, CheckCircle2, Clock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, fmtTL } from "@/lib/api";

function Countdown({ seconds }) {
  const m = Math.floor(seconds / 60), s = seconds % 60;
  return <span className="font-mono">{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}</span>;
}

export default function Buy() {
  const [services, setServices] = useState([]);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null); // service for country modal
  const [countries, setCountries] = useState([]);
  const [loadingC, setLoadingC] = useState(false);
  const [buying, setBuying] = useState(null);
  const [active, setActive] = useState(null); // activation screen
  const pollRef = useRef(null);

  useEffect(() => { api.get("/public/services").then(({ data }) => setServices(data.services || [])); }, []);

  const openService = async (s) => {
    setPicked(s); setCountries([]); setLoadingC(true);
    try {
      const { data } = await api.get(`/public/countries?service=${s.code}`);
      setCountries(data.countries || []);
    } finally { setLoadingC(false); }
  };

  const buy = async (c) => {
    setBuying(c.country);
    try {
      const { data } = await api.post("/activations", { service: picked.code, country: c.country });
      setActive(data); setPicked(null);
      window.dispatchEvent(new Event("wallet-refresh"));
      toast.success("Numara alındı!");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setBuying(null); }
  };

  // poll activation
  useEffect(() => {
    if (!active || active.status !== "waiting") return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/activations/${active.id}`);
        setActive(data);
        if (data.status !== "waiting") clearInterval(pollRef.current);
      } catch {}
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [active?.id, active?.status]);

  // local ticking
  useEffect(() => {
    if (!active || active.status !== "waiting") return;
    const t = setInterval(() => setActive((a) => a ? { ...a, seconds_left: Math.max(0, a.seconds_left - 1), can_cancel: a.seconds_left - 1 > 120 } : a), 1000);
    return () => clearInterval(t);
  }, [active?.id, active?.status]);

  const cancel = async () => {
    try { await api.post(`/activations/${active.id}/cancel`); window.dispatchEvent(new Event("wallet-refresh")); toast.success("İptal edildi, ücret iade edildi"); setActive(null); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const filtered = services.filter((s) => s.name.toLowerCase().includes(q.toLowerCase()));

  // ---------- ACTIVATION SCREEN ----------
  if (active) {
    const done = active.status === "received" || active.status === "done";
    return (
      <div className="anim-fadeup">
        <button onClick={() => setActive(null)} className="flex items-center gap-2 text-sm text-panel-secondary mb-4"><ArrowLeft size={16} /> Geri</button>
        <div className="card p-6 text-center" data-testid="activation-screen">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src={active.icon} alt="" className="h-10 w-10 rounded-xl bg-white p-1 object-contain" />
            <div className="text-left">
              <div className="font-semibold">{active.service_name}</div>
              <div className="text-xs text-panel-muted">{active.country_name}</div>
            </div>
          </div>

          <div className="rounded-2xl bg-panel-input border border-panel-border p-4 mb-4">
            <div className="text-xs text-panel-muted mb-1">Numaranız</div>
            <div className="flex items-center justify-center gap-2">
              <span className="font-mono text-2xl font-bold" data-testid="activation-phone">+{active.phone}</span>
              <button onClick={() => { navigator.clipboard.writeText(active.phone); toast.success("Kopyalandı"); }} className="text-brand-500"><Copy size={18} /></button>
            </div>
          </div>

          {!done && (
            <>
              <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/15 text-amber-500 px-4 py-2 text-sm font-semibold mb-4" data-testid="activation-timer">
                <Clock size={16} /> Kalan Süre: <Countdown seconds={active.seconds_left} />
              </div>
              <div className="flex items-center justify-center gap-2 text-panel-secondary text-sm mb-5">
                <Loader2 size={16} className="animate-spin" /> SMS onay kodu bekleniyor...
              </div>
              <button onClick={cancel} disabled={!active.can_cancel} data-testid="activation-cancel"
                className="w-full rounded-xl h-12 font-semibold border border-panel-border disabled:opacity-40 hover:border-red-500 hover:text-red-500 transition-colors">
                {active.can_cancel ? "İptal Et ve İade Al" : "Son 2 dk — iptal edilemez"}
              </button>
            </>
          )}

          {done && (
            <div className="anim-pop">
              <div className="mx-auto h-16 w-16 rounded-full bg-emerald-500/15 text-emerald-500 grid place-items-center mb-3"><CheckCircle2 size={34} /></div>
              <div className="text-xs text-panel-muted mb-1">Onay Kodunuz</div>
              <div className="flex items-center justify-center gap-2 mb-4">
                <span className="font-mono text-3xl font-extrabold text-emerald-500" data-testid="activation-code">{active.code}</span>
                <button onClick={() => { navigator.clipboard.writeText(active.code); toast.success("Kopyalandı"); }} className="text-brand-500"><Copy size={20} /></button>
              </div>
              <button onClick={() => setActive(null)} className="btn-blue w-full rounded-xl h-12 font-semibold">Tamam</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---------- SERVICE GRID ----------
  return (
    <div>
      <h1 className="font-head text-3xl font-bold tracking-tight">Servis Seç</h1>
      <p className="text-panel-secondary text-sm mt-1">SMS onay almak istediğin servisi seç.</p>
      <div className="relative mt-4 mb-5">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-panel-muted" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="buy-search" placeholder="Servis ara (WhatsApp, Telegram...)"
          className="pinput w-full rounded-xl h-12 pl-10 pr-4 outline-none focus:border-brand-500" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {filtered.map((s) => (
          <button key={s.code} onClick={() => openService(s)} data-testid={`buy-service-${s.code}`}
            className="card p-4 flex items-center gap-3 text-left hover:border-brand-500 hover:-translate-y-0.5 transition-all relative">
            {s.popular && <span className="absolute top-1.5 right-1.5 text-[8px] font-bold bg-brand/15 text-brand-500 rounded-full px-1.5 py-0.5">Popüler</span>}
            <img src={s.icon} alt="" className="h-10 w-10 rounded-xl bg-white p-1 object-contain shrink-0" onError={(e) => (e.target.style.visibility = "hidden")} />
            <div className="min-w-0">
              <div className="font-semibold text-sm truncate">{s.name}</div>
              <div className="text-xs text-brand-500 font-semibold">{fmtTL(s.price_from)}'den</div>
            </div>
          </button>
        ))}
      </div>

      {/* COUNTRY MODAL */}
      {picked && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4 anim-fadein" onClick={() => setPicked(null)}>
          <div className="bg-panel-card border border-panel-border w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl max-h-[85vh] flex flex-col anim-fadeup" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-panel-border">
              <div className="flex items-center gap-3">
                <img src={picked.icon} alt="" className="h-9 w-9 rounded-xl bg-white p-1 object-contain" />
                <div><div className="font-semibold">{picked.name}</div><div className="text-xs text-panel-muted">Ülke seç ve numara al</div></div>
              </div>
              <button onClick={() => setPicked(null)} data-testid="country-modal-close" className="h-9 w-9 grid place-items-center rounded-lg card"><X size={18} /></button>
            </div>
            <div className="p-4 overflow-auto space-y-2">
              {loadingC && <div className="flex justify-center py-10"><Loader2 className="animate-spin text-brand-500" /></div>}
              {!loadingC && countries.map((c) => (
                <div key={c.country} className="flex items-center justify-between rounded-xl bg-panel-input border border-panel-border p-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <img src={c.flag} alt="" className="h-4 w-6 object-cover rounded-sm shrink-0" onError={(e) => (e.target.style.visibility = "hidden")} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate flex items-center gap-1.5">
                        {c.name}
                        {c.popular && <Flame size={12} className="text-brand-500" />}
                        {c.high_success && <Star size={12} className="text-emerald-500" />}
                      </div>
                      <div className="text-[11px] text-panel-muted">Stok: {c.stock?.toLocaleString("tr-TR")}</div>
                    </div>
                  </div>
                  <button onClick={() => buy(c)} disabled={buying === c.country || c.stock === 0} data-testid={`buy-country-${c.country}`}
                    className="btn-blue rounded-xl px-4 h-10 text-sm font-semibold flex items-center gap-2 shrink-0">
                    {buying === c.country ? <Loader2 size={15} className="animate-spin" /> : fmtTL(c.price)}
                  </button>
                </div>
              ))}
              {!loadingC && countries.length === 0 && <div className="text-center text-panel-muted py-10 text-sm">Bu servis için ülke bulunamadı.</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
